/**
 * An entity's mentions per month as a row of bars (PRD-0075 R-C1): one bar per calendar month,
 * empty months included, so a quiet year reads as a gap. Presentational; the months come in
 * oldest first, already filled.
 */
import { useTranslation } from 'react-i18next'

export interface ActivityMonth {
  /** `YYYY-MM`. */
  month: string
  count: number
}

const HEIGHT = 24
const BAR = 4
const GAP = 1

export function ActivitySparkline({ months }: { months: readonly ActivityMonth[] }) {
  const { t } = useTranslation()
  if (months.length === 0) return null
  const peak = Math.max(1, ...months.map(m => m.count))
  const width = months.length * (BAR + GAP) - GAP
  const first = months[0].month
  const last = months[months.length - 1].month
  const label = t('investigations.entityDetail.history.header.activity', { from: first, to: last })
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="h-6 w-full text-primary/70"
    >
      <title>{label}</title>
      {months.map((m, index) => {
        // A month with a mention is never thinner than a pixel; an empty one is a faint baseline.
        const height = m.count > 0 ? Math.max(2, (m.count / peak) * HEIGHT) : 1
        return (
          <rect
            key={m.month}
            x={index * (BAR + GAP)}
            y={HEIGHT - height}
            width={BAR}
            height={height}
            rx={1}
            fill="currentColor"
            opacity={m.count > 0 ? 1 : 0.25}
          >
            <title>
              {t('investigations.entityDetail.history.header.activityMonth', {
                month: m.month,
                count: m.count,
              })}
            </title>
          </rect>
        )
      })}
    </svg>
  )
}
